import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
  Share as ShareIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Forum as ForumIcon,
  ConnectWithoutContact as ConnectIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Send as SendIcon,
  EmojiEmotions as EmojiIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Empty State when no conversation is selected
const EmptyConversationState = ({ onFindPeople }) => {
  const navigate = useNavigate();

  const tips = [
    {
      icon: <QuestionAnswerIcon color="primary" />,
      title: "Professional Communication",
      description: "Keep conversations professional and business-focused"
    },
    {
      icon: <TrendingUpIcon color="primary" />,
      title: "Build Relationships", 
      description: "Use conversations to strengthen your business network"
    },
    {
      icon: <ConnectIcon color="primary" />,
      title: "Be Responsive",
      description: "Quick responses help build trust and reliability"
    },
    {
      icon: <BusinessIcon color="primary" />,
      title: "Share Opportunities",
      description: "Discuss potential collaborations and business opportunities"
    }
  ];

  const quickActions = [
    {
      icon: <PersonAddIcon />,
      title: "Find People to Message",
      description: "Search for colleagues and business partners",
      action: () => onFindPeople()
    },
    {
      icon: <GroupsIcon />,
      title: "Browse Your Network",
      description: "Message people you're already connected with",
      action: () => navigate('/network')
    },
    {
      icon: <BusinessIcon />,
      title: "Explore Businesses",
      description: "Connect with businesses in your industry",
      action: () => navigate('/business-search')
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 3,
      textAlign: 'center'
    }}>
      {/* Main Empty State */}
      <Box sx={{ mb: 4, maxWidth: 500 }}>
        <ChatIcon sx={{ fontSize: 120, color: 'primary.main', mb: 3, opacity: 0.7 }} />
        <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
          Select a Conversation
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Choose a conversation from the left to start messaging
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Build stronger business relationships through direct communication. 
          Share ideas, discuss opportunities, and grow your professional network.
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<PersonAddIcon />}
          onClick={onFindPeople}
          sx={{ mb: 4 }}
        >
          Start New Conversation
        </Button>
      </Box>

      {/* Communication Tips */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none', maxWidth: 600, width: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          💬 Communication Tips
        </Typography>
        <Grid container spacing={2}>
          {tips.map((tip, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card variant="outlined" sx={{ p: 2, height: '100%', border: 'none', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    {tip.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tip.description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Box sx={{ maxWidth: 500, width: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
          Get Started
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {quickActions.map((action, index) => (
            <Card 
              key={index}
              variant="outlined" 
              sx={{ 
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: 'grey.50',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
              onClick={action.action}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  {action.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Box>
                <ArrowForwardIcon color="action" />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Active conversation state
const ActiveConversationState = ({ 
  selectedUser, 
  chatHistory, 
  messageInput, 
  onMessageChange, 
  onSendMessage, 
  onBackToUserList,
  isMobile,
  ChatMessageDetail // Pass the existing ChatMessageDetail component
}) => {
  return (
    <Box sx={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
      {/* Header with back button for mobile */}
      {isMobile && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button 
              variant="text" 
              onClick={onBackToUserList}
              sx={{ color: 'primary.main', minWidth: 'auto', p: 0.5 }}
            >
              ← Back
            </Button>
            <Avatar src={selectedUser?.photoUrl} sx={{ width: 32, height: 32 }}>
              {selectedUser?.displayName?.charAt(0)}
            </Avatar>
            <Typography variant="h6">
              {selectedUser?.displayName}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={selectedUser?.photoUrl} sx={{ width: 40, height: 40 }}>
              {selectedUser?.displayName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {selectedUser?.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Online now
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          maxHeight: 'calc(100vh - 350px)',
          overflowY: 'auto',
          p: 2,
          '&::-webkit-scrollbar': { width: '0.4em' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#888' },
        }}
      >
        {chatHistory && chatHistory.length > 0 ? (
          chatHistory.map((message) => (
            <ChatMessageDetail key={message.msgId} message={message} />
          ))
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            color: 'text.secondary'
          }}>
            <ForumIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              Start the conversation
            </Typography>
            <Typography variant="body2">
              This is the beginning of your conversation with {selectedUser?.displayName}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Message Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          placeholder={`Message ${selectedUser?.displayName || 'user'}...`}
          sx={{ backgroundColor: '#f5f5f5' }}
          value={messageInput}
          onChange={onMessageChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSendMessage();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <EmojiIcon sx={{ color: 'text.secondary', cursor: 'pointer' }} />
                  <SendIcon 
                    sx={{ 
                      color: messageInput.trim() ? 'primary.main' : 'text.secondary',
                      cursor: messageInput.trim() ? 'pointer' : 'default'
                    }}
                    onClick={messageInput.trim() ? onSendMessage : undefined}
                  />
                </Box>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Box>
    </Box>
  );
};

// Main component for the chat detail column
const ChatDetailColumn = ({
  selectedUser,
  chatHistory,
  messageInput,
  onMessageChange,
  onSendMessage,
  onBackToUserList,
  onFindPeople,
  isMobile = false,
  ChatMessageDetail // Pass the existing ChatMessageDetail component
}) => {
  if (!selectedUser) {
    return (
      <EmptyConversationState 
        onFindPeople={onFindPeople}
      />
    );
  }

  return (
    <ActiveConversationState
      selectedUser={selectedUser}
      chatHistory={chatHistory}
      messageInput={messageInput}
      onMessageChange={onMessageChange}
      onSendMessage={onSendMessage}
      onBackToUserList={onBackToUserList}
      isMobile={isMobile}
      ChatMessageDetail={ChatMessageDetail}
    />
  );
};

export default ChatDetailColumn;